import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/atoms/button';
import type { RootState } from '@store/root-reducer';
import { getImageUrl } from '@utils/get-image-url';

import type { ZkProof } from '../types';
import { ZkProofBlock } from '../zk-proof-block';

type SdkOptions = NonNullable<RootState['docsVerification']['sdkOptions']>;

type WidgetFinishProps = {
    sdkOptions?: SdkOptions;
    timeToRedirect: number;
    redirectToLink: () => void;
    isTelegram: boolean;
    isWeb3?: boolean;
    botName: string | null;
    isLoading: boolean;
    verified?: boolean;
    zkProof?: ZkProof;
    zkProofError: string;
};

export const WidgetFinish: React.FC<WidgetFinishProps> = ({
    sdkOptions,
    timeToRedirect,
    redirectToLink,
    isTelegram,
    isWeb3,
    botName,
    isLoading,
    verified,
    zkProof,
    zkProofError,
}) => {
    const { t } = useTranslation();

    return (
        <React.Fragment>
            {sdkOptions?.settings?.finish_screen_settings?.enabled ? (
                <React.Fragment>
                    <div className='icon icon-success' style={{ textAlign: 'center' }}>
                        <img
                            src={getImageUrl('/images/sdk/verification/success-icon.png')}
                            alt='Success icon'
                        />
                    </div>
                    <div className='header'>
                        <h3 className='finish-title'>
                            {sdkOptions?.settings?.finish_screen_settings.title}
                        </h3>
                        <p className='finish-description'>
                            {sdkOptions?.settings?.finish_screen_settings.main_text}
                        </p>
                    </div>
                    {sdkOptions?.settings?.finish_screen_settings.redirect_link && (
                        <React.Fragment>
                            {sdkOptions?.settings?.finish_screen_settings?.redirect_warning && (
                                <div className='finish-description'>
                                    {sdkOptions?.settings?.finish_screen_settings?.redirect_warning}
                                </div>
                                /*        commented out till V2 of Finish screen
                                (
                                <div
                                    className='finish-description'
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'docVerificationWidget.finish.description.youWillBeRedirected',
                                            {
                                                count: timeToRedirect / 1000,
                                            },
                                        ),
                                    }}
                                /> */
                            )}

                            {sdkOptions?.settings?.finish_screen_settings.cta && (
                                <Button
                                    variant='primary'
                                    className='button-redirect'
                                    onClick={redirectToLink}
                                    size='xl'
                                >
                                    {sdkOptions?.settings?.finish_screen_settings.cta}{' '}
                                </Button>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <div className='icon icon-success' style={{ textAlign: 'center' }}>
                        <img
                            src={getImageUrl('/images/sdk/verification/success-icon.png')}
                            alt='Success icon'
                        />
                    </div>
                    <div className='header'>
                        <h3 className='finish-title'>
                            <span>{t('common.thankYou')}</span>
                            {'\n'}
                            {!isTelegram && (
                                <span className='alt-title'>
                                    {isWeb3
                                        ? t(
                                              'docVerificationWidget.finish.subtitle.youUploadedAllDocuments',
                                          )
                                        : t(
                                              'docVerificationWidget.finish.title.youUploadedAllDocuments',
                                          )}
                                </span>
                            )}
                        </h3>
                        {sdkOptions?.redirectUrl && (
                            <React.Fragment>
                                <div
                                    className='finish-description'
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            'docVerificationWidget.finish.description.youWillBeRedirected',
                                            {
                                                count: timeToRedirect / 1000,
                                            },
                                        ),
                                    }}
                                />
                                <Button
                                    variant='primary'
                                    className='button-redirect'
                                    onClick={redirectToLink}
                                    size='xl'
                                >
                                    {t('common.goBack')}
                                </Button>
                            </React.Fragment>
                        )}
                        {!sdkOptions?.redirectUrl && isTelegram && (
                            <React.Fragment>
                                <p className='finish-description'>
                                    {t('docVerificationWidget.finish.description.telegram')}
                                </p>
                                {!!botName && (
                                    <Button
                                        variant='primary'
                                        className='button-redirect'
                                        size='xl'
                                        to={`tg://resolve?domain=${botName}`}
                                    >
                                        {t('docVerificationWidget.finish.goToTelegram')}
                                    </Button>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </React.Fragment>
            )}
            <ZkProofBlock
                isWeb3={isWeb3}
                isLoading={isLoading}
                verified={verified}
                zkProof={zkProof}
                zkProofError={zkProofError}
            />
        </React.Fragment>
    );
};
